import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    CustomInput,
    Label,
    Row,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faBuilding,
    faChartBar,
    faCloudUploadAlt,
    faExclamationTriangle,
    faExternalLinkAlt,
    faFileAlt,
    faShieldAlt,
    faSpinner,
    faUser,
} from '@fortawesome/free-solid-svg-icons';
import { SessionContext } from '../../shared/session';
import Header from '../../shared/Header';
import Requester from '../../shared/Requester';
import {
    getEbrPorSujeto,
    getSujetoById,
    listNodosByOperacion,
    listOperaciones,
    listOperacionSujetos,
    listPaises,
    resolvePldDocumentoFetchUrl,
    updateSujeto,
    uploadDocumento,
} from '../api/pldApi';
import ModalIdentificacion from './ModalIdentificacion';
import ModalFormatoBC from './ModalFormatoBC';
import HojaFrontal from '../pdf/HojaFrontal';
import HojaFormatoBC from '../pdf/HojaFormatoBC';
import { pdf } from '@react-pdf/renderer';
import { determinarPerfilSujeto } from '../utils/determinarPerfil';

const REPORTE_PLD_DOC_ID = 'reporte_pld_listas';

// ── Configuración de visualización para los 6 factores EBR ──────────────────
const EBR_FACTOR_LABELS = {
    nacionalidad_residencia: 'Geografía / Nacionalidad',
    giro_actividad_ebr:      'Giro / Actividad Económica',
    estructura_juridica:     'Estructura Jurídica',
    antiguedad_empresa:      'Antigüedad de la Empresa',
    calidad_pep:             'Calidad PEP',
    coincidencia_listas:     'Coincidencia en Listas',
};

const EBR_NIVEL_CONFIG = {
    ALTO:  { color: 'danger',  texto: 'Alto' },
    MEDIO: { color: 'warning', texto: 'Medio' },
    BAJO:  { color: 'success', texto: 'Bajo' },
};

const EBR_PUNTOS_CONFIG = {
    3.0: { color: 'danger',  label: '3.0' },
    2.0: { color: 'warning', label: '2.0' },
    1.0: { color: 'success', label: '1.0' },
};

function formatFechaEbr(isoStr) {
    if (!isoStr) return '—';
    try {
        return new Date(isoStr).toLocaleString('es-MX', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch (_) {
        return isoStr;
    }
}

/**
 * Card que muestra el perfil de riesgo inherente EBR más reciente del sujeto
 * en la operación actual.
 */
function CardEbrInherente({ registro }) {
    if (!registro) return null;

    const resultado = registro.resultado_inherente || {};
    const factores  = registro.factores || {};
    const nivel     = resultado.nivel_riesgo_inherente || '';
    const nivelCfg  = EBR_NIVEL_CONFIG[nivel] || { color: 'secondary', texto: nivel };
    const usuario   = registro.usuario;
    const overrides = resultado.overrides_activados || [];

    return (
        <Card className="shadow-sm mb-4">
            <CardHeader
                className={`d-flex align-items-center justify-content-between bg-${nivelCfg.color} text-white py-2`}
            >
                <span className="font-weight-bold small text-uppercase">
                    <FontAwesomeIcon icon={faChartBar} className="mr-2" />
                    Perfil de Riesgo Inherente EBR
                </span>
                <Badge color="light" className={`text-${nivelCfg.color} font-weight-bold`}>
                    {nivelCfg.texto}
                </Badge>
            </CardHeader>
            <CardBody>
                {/* Fila principal: scores + factores */}
                <Row>
                    {/* Columna izquierda: scores */}
                    <Col xs="12" md="4" className="mb-3">
                        <div className="border rounded p-3 bg-light h-100">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="small text-muted">Score promedio</span>
                                <strong>{resultado.score_promedio_calculado ?? '—'}</strong>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="small text-muted">Score final</span>
                                <strong className={`text-${nivelCfg.color}`}>
                                    {resultado.score_final_con_override ?? '—'}
                                </strong>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="small text-muted">Nivel de riesgo</span>
                                <Badge color={nivelCfg.color} pill>{nivelCfg.texto}</Badge>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="small text-muted">EDD / SOOF / SOOW</span>
                                <Badge
                                    color={resultado.requiere_edd_soof_soow ? 'danger' : 'success'}
                                    pill
                                >
                                    {resultado.requiere_edd_soof_soow ? 'Sí' : 'No'}
                                </Badge>
                            </div>
                        </div>
                    </Col>
                    {/* Columna derecha: factores individuales */}
                    <Col xs="12" md="8" className="mb-3">
                        {Object.entries(EBR_FACTOR_LABELS).map(([clave, label]) => {
                            const f = factores[clave] || {};
                            const pts = f.puntos_factor;
                            const ptsCfg = EBR_PUNTOS_CONFIG[pts] || { color: 'secondary', label: pts ?? '—' };
                            const hasOverride = !!f.flag_override_alto_riesgo;
                            return (
                                <div
                                    key={clave}
                                    className="d-flex align-items-center justify-content-between py-1 border-bottom"
                                >
                                    <span className="small text-secondary" style={{ flex: 1 }}>
                                        {label}
                                        {hasOverride && (
                                            <FontAwesomeIcon
                                                icon={faExclamationTriangle}
                                                className="ml-1 text-danger"
                                                title={f.motivo_override || 'Override activo'}
                                            />
                                        )}
                                    </span>
                                    <Badge color={ptsCfg.color} pill className="ml-2">
                                        {ptsCfg.label}
                                    </Badge>
                                </div>
                            );
                        })}
                    </Col>
                </Row>
                {/* Overrides — sección separada, ancho completo */}
                {overrides.length > 0 && (
                    <div className="p-2 mb-2 border border-danger rounded bg-light">
                        <div className="small font-weight-bold text-danger mb-1">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                            Overrides automáticos activados
                        </div>
                        <div>
                            {overrides.map((o) => (
                                <Badge
                                    key={o}
                                    color="danger"
                                    pill
                                    className="mr-1 mb-1 small"
                                >
                                    {o.replace(/_/g, ' ')}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}
                {/* Pie: fecha y usuario */}
                <div className="text-right text-muted" style={{ fontSize: '0.72rem' }}>
                    Evaluado el {formatFechaEbr(registro.timestamp_utc)}
                    {usuario && ` · ${usuario.nombre || usuario.correo || ''}`}
                </div>
            </CardBody>
        </Card>
    );
}

function nombreCompletoSujeto(s) {
    if (!s) return '';
    return `${s.nombre || ''} ${s.paterno || ''} ${s.materno || ''}`.trim();
}

function formatFechaReportePld(iso) {
    if (!iso) return 'Consulta de listas Q-Detect';
    try {
        return `Consulta: ${new Date(iso).toLocaleString('es-MX', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        })}`;
    } catch (_) {
        return 'Consulta de listas Q-Detect';
    }
}

export default function ExpedienteSujeto({ unidadId: unidadIdProp }) {
    const { unidad: unidadRoute, operacionId, sujetoId, relacionId } = useParams();
    const history = useHistory();
    const session = useContext(SessionContext);
    const esInmobiliaria = session.unidad?.actividad === 'inmobiliaria';

    const unidadId =
        unidadIdProp ||
        unidadRoute ||
        (session.unidad && session.unidad._id) ||
        null;

    const [sujeto, setSujeto] = useState(null);
    const [rolSujeto, setRolSujeto] = useState('');
    const [esBC, setEsBC] = useState(false);
    const [origenBC, setOrigenBC] = useState('');
    const [cargando, setCargando] = useState(true);
    const [docsSujeto, setDocsSujeto] = useState({});
    const [subiendoId, setSubiendoId] = useState(null);
    const [showModalId, setShowModalId] = useState(false);
    const [showModalBc, setShowModalBc] = useState(false);
    const [empresaClienteSugerida, setEmpresaClienteSugerida] = useState('');
    const [pldReporteDisponible, setPldReporteDisponible] = useState(false);
    const [pldReporteFecha, setPldReporteFecha] = useState(null);
    const [ebrRegistro, setEbrRegistro] = useState(null);

    const puedeSujetos =
        session?.permisos?.admin || session?.permisos?.sujetosPLD === 'write';
    const puedeDocumentos =
        session?.permisos?.admin || session?.permisos?.documentosPLD === 'write';

    useEffect(() => {
        if (!unidadId || !operacionId || !sujetoId) {
            setCargando(false);
            return undefined;
        }
        let cancelled = false;
        async function cargarDatos() {
            setCargando(true);
            try {
                const [dataSujeto, nodos, ops, sujetosOp, ebr] = await Promise.all([
                    getSujetoById(unidadId, sujetoId),
                    listNodosByOperacion(unidadId, operacionId),
                    listOperaciones(unidadId).catch(() => []),
                    listOperacionSujetos(unidadId, operacionId).catch(() => []),
                    getEbrPorSujeto(operacionId, sujetoId).catch(() => null),
                ]);
                if (!cancelled) setEbrRegistro(ebr || null);
                if (cancelled) return;
                if (Array.isArray(ops)) {
                    const op = ops.find((o) => String(o.id) === String(operacionId));
                    if (op && op.denominacion) {
                        setEmpresaClienteSugerida(op.denominacion);
                    }
                }
                if (dataSujeto) {
                    setSujeto(dataSujeto);
                    setDocsSujeto(dataSujeto.documentos_status || {});
                    const nombreCompleto = nombreCompletoSujeto(dataSujeto);
                    if (nombreCompleto) {
                        try {
                            const resumenRes = await Requester.get('pld/resumen', {
                                params: { nombreCompleto },
                            });
                            if (!cancelled && resumenRes && resumenRes.data && resumenRes.data.existe === true) {
                                setPldReporteDisponible(true);
                                setPldReporteFecha(resumenRes.data.requestDate || null);
                            } else if (!cancelled) {
                                setPldReporteDisponible(false);
                                setPldReporteFecha(null);
                            }
                        } catch (_) {
                            if (!cancelled) {
                                setPldReporteDisponible(false);
                                setPldReporteFecha(null);
                            }
                        }
                    } else if (!cancelled) {
                        setPldReporteDisponible(false);
                        setPldReporteFecha(null);
                    }
                }
                // El rol viene de la relación sujeto-operación (/sujetos), identificada
                // por relacionId (id_relacion). Si no viene en la URL, cae al primer
                // registro con ese sujeto_id como fallback.
                // es_bc y origen_bc vienen del nodo (/nodos).
                const sujetoEnOp = Array.isArray(sujetosOp)
                    ? (relacionId
                        ? sujetosOp.find((s) => String(s.id_relacion) === String(relacionId))
                        : sujetosOp.find((s) => String(s.id_sujeto) === String(sujetoId)))
                    : null;

                setRolSujeto(sujetoEnOp?.rol || '');
                setEsBC(!!(sujetoEnOp?.es_bc));
                setOrigenBC(sujetoEnOp?.origen_bc || '');
            } catch (e) {
                if (!cancelled) {
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'No se pudo cargar la ficha del sujeto.',
                    });
                }
            } finally {
                if (!cancelled) setCargando(false);
            }
        }
        cargarDatos();
        return () => {
            cancelled = true;
        };
    }, [unidadId, operacionId, sujetoId, relacionId]);

    const perfilActual = sujeto?.tipo_persona?.toLowerCase() === 'moral' ? 'SOCIEDAD' : determinarPerfilSujeto(rolSujeto, esBC, esInmobiliaria);

    function obtenerChecklist() {
        if (!sujeto || perfilActual === 'SOLO_DATOS') return [];
        let docs = [];
        if (perfilActual === 'SOCIEDAD') {
            docs = [
                {
                    id: 'acta_constitutiva',
                    titulo: 'Acta constitutiva',
                    desc: 'Con datos de RPC',
                },
                {
                    id: 'esquema_accionario',
                    titulo: 'Esquema accionario',
                    // Al subirlo, debe guardarse la fecha (debería actualizarse cada año)
                    // Revisar que no aparece en el Master PDF
                },
                {
                    id: 'csf_pm',
                    titulo: 'CIF',
                    desc: 'Cédula de Identificación Fiscal',
                },
                {
                    id: 'poder',
                    titulo: 'Poder del representante',
                    desc: 'Facultades suficientes',
                    // Subir nuevo poder o revisar que no se ha revocado.
                },
                {
                    id: 'domicilio_pm',
                    titulo: 'Comprobante de domicilio',
                    desc: 'No mayor a 3 meses',
                    // Revisar que el comprobante siga válido o solicitar uno nuevo.
                },
            ];
        } else if (perfilActual === 'DELEGADO') {
            docs = [
                {
                    id: 'id_oficial',
                    titulo: 'Identificación oficial',
                    desc: 'INE, pasaporte o cédula',
                },
                {
                    id: 'domicilio',
                    titulo: 'Comprobante de domicilio',
                    desc: 'No mayor a 3 meses',
                },
                {
                    id: 'poder',
                    titulo: 'Carta poder / nombramiento',
                    desc: 'Facultades',
                },
            ];
        } else {
            docs = [
                {
                    id: 'id_oficial',
                    titulo: 'Identificación oficial',
                    desc: 'INE, pasaporte o cédula',
                },
                { id: 'curp', titulo: 'CURP', desc: 'Clave Única de Registro de Población' },
                {
                    id: 'rfc',
                    titulo: 'CIF',
                    desc: 'Cédula de Identificación Fiscal',
                },
                {
                    id: 'domicilio',
                    titulo: 'Comprobante de domicilio',
                    desc: 'No mayor a 3 meses',
                },
            ];
        }

        if (sujeto.pld_pais_nacionalidad && sujeto.pld_pais_nacionalidad !== 'MEX') {
            docs = docs.filter((doc) => doc.id !== 'curp' && doc.id !== 'id_oficial');
            docs.unshift({
                id: 'doc_legal_estancia',
                titulo: 'Documento legal de estancia',
                desc: '',
            });
        }

        if (perfilActual !== 'N/A' && !esBC) {
            docs.push({
                id: 'formato_id_fisico',
                titulo: 'Formato de identificación',
                desc: 'Físico firmado',
            });
        }

        if (esBC) {
            docs.push({
                id: 'formato_bc_fisico',
                titulo: 'Declaración dueño beneficiario',
                desc: 'Físico firmado',
            });
        }

        if (pldReporteDisponible) {
            docs.push({
                id: REPORTE_PLD_DOC_ID,
                titulo: 'Reporte consulta PLD (listas)',
                desc: formatFechaReportePld(pldReporteFecha),
                esReportePld: true,
            });
        }

        return docs;
    }

    async function abrirReportePldPdf() {
        const nombreCompleto = nombreCompletoSujeto(sujeto);
        if (!nombreCompleto) return;
        try {
            const pdfResponse = await Requester.get('pld', {
                params: { nombreCompleto },
                responseType: 'blob',
                validateStatus: (status) => status === 200 || status === 404,
            });
            if (pdfResponse.status === 200) {
                const url = window.URL.createObjectURL(pdfResponse.data);
                window.open(url, '_blank', 'noopener,noreferrer');
                window.setTimeout(() => window.URL.revokeObjectURL(url), 30000);
                return;
            }
            await Swal.fire({
                icon: 'warning',
                title: 'Reporte PLD',
                text: 'No se encontró el PDF del reporte para este sujeto.',
            });
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Reporte PLD',
                text: err.message || 'No se pudo abrir el reporte.',
            });
        }
    }

    async function handleUpload(e, docId) {
        const file = e.target.files && e.target.files[0];
        if (!file || !unidadId) return;
        setSubiendoId(docId);
        try {
            const res = await uploadDocumento(unidadId, sujetoId, docId, file);
            if (res && res.success) {
                setDocsSujeto(res.nuevoStatus || {});
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: 'Subida',
                    text: (res && res.error) || 'No se pudo subir el archivo.',
                });
            }
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Subida',
                text:
                    (err &&
                        err.response &&
                        err.response.data &&
                        err.response.data.error) ||
                    err.message ||
                    'Error al subir',
            });
        } finally {
            setSubiendoId(null);
            e.target.value = '';
        }
    }

    async function togglePropiedad(docId, propiedad) {
        if (!puedeDocumentos) return;
        const docActual = docsSujeto[docId] || {};
        const nuevoValor = !docActual[propiedad];
        let updateObj = { ...docActual, [propiedad]: nuevoValor };
        if (propiedad === 'cotejado' && nuevoValor && !docActual.url) {
            updateObj = { ...updateObj, fisico: true };
        }
        const prev = docsSujeto;
        const nuevoStatus = { ...docsSujeto, [docId]: updateObj };
        setDocsSujeto(nuevoStatus);
        try {
            await updateSujeto(unidadId, sujetoId, { documentos_status: nuevoStatus });
        } catch (err) {
            setDocsSujeto(prev);
            await Swal.fire({
                icon: 'error',
                title: 'No se pudo guardar',
                text:
                    (err &&
                        err.response &&
                        err.response.data &&
                        err.response.data.error) ||
                    err.message ||
                    'Error',
            });
        }
    }

    async function cotejarTodos() {
        if (!puedeDocumentos) return;
        const ok = await Swal.fire({
            icon: 'warning',
            title: 'Sellar documentos',
            text: 'Al sellar todos, declara bajo protesta que ha cotejado los documentos contra sus originales.',
            showCancelButton: true,
            confirmButtonText: 'Sí, sellar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#198754',
        });
        if (!ok.isConfirmed) return;
        const checklist = obtenerChecklist();
        const nuevoStatus = { ...docsSujeto };
        checklist.forEach((doc) => {
            if (!nuevoStatus[doc.id]) nuevoStatus[doc.id] = {};
            const esReportePld = doc.id === REPORTE_PLD_DOC_ID;
            if (!nuevoStatus[doc.id].url && !esReportePld) {
                nuevoStatus[doc.id].fisico = true;
            }
            nuevoStatus[doc.id].cotejado = true;
        });
        const prev = docsSujeto;
        setDocsSujeto(nuevoStatus);
        try {
            await updateSujeto(unidadId, sujetoId, { documentos_status: nuevoStatus });
        } catch (err) {
            setDocsSujeto(prev);
            await Swal.fire({
                icon: 'error',
                title: 'No se pudo guardar',
                text:
                    (err &&
                        err.response &&
                        err.response.data &&
                        err.response.data.error) ||
                    err.message ||
                    'Error',
            });
        }
    }

    async function generarPDFFormato() {
        if (!unidadId || !operacionId || !sujetoId || !sujeto) return;
        try {
            const [ops, sujetosTabla, paisesCat] = await Promise.all([
                listOperaciones(unidadId),
                listOperacionSujetos(unidadId, operacionId),
                listPaises(unidadId).catch(() => []),
            ]);
            const operacion = Array.isArray(ops)
                ? ops.find((o) => String(o.id) === String(operacionId))
                : null;
            if (!operacion) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Operación',
                    text: 'No se encontró la operación para armar el formato.',
                });
                return;
            }
            const blob = await pdf(
                <HojaFrontal
                    operacion={operacion}
                    sujetos={Array.isArray(sujetosTabla) ? sujetosTabla : []}
                    sujetoDetalle={sujeto}
                    rolEnOperacion={rolSujeto}
                    paises={Array.isArray(paisesCat) ? paisesCat : []}
                />
            ).toBlob();
            const nombreArchivo = `formato-id-${(sujeto.rfc || sujetoId).replace(/[^\w.-]/g, '_')}.pdf`;
            const file = new File([blob], nombreArchivo, { type: 'application/pdf' });
            const res = await uploadDocumento(unidadId, sujetoId, 'formato_id_fisico', file);
            if (res && res.success) {
                setDocsSujeto(res.nuevoStatus || {});
                setSujeto((prev) =>
                    prev ? { ...prev, documentos_status: res.nuevoStatus || {} } : prev
                );
                await Swal.fire({
                    icon: 'success',
                    title: 'Formato generado',
                    text: 'El PDF se generó y quedó asociado al expediente como formato de identificación.',
                });
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: 'Generación',
                    text: (res && res.error) || 'No se pudo guardar el PDF en el servidor.',
                });
            }
        } catch (err) {
            console.error(err);
            await Swal.fire({
                icon: 'error',
                title: 'Generación',
                text:
                    (err &&
                        err.response &&
                        err.response.data &&
                        err.response.data.error) ||
                    err.message ||
                    'Error al generar el formato.',
            });
        }
    }

    async function generarPDFFormatoBC() {
        if (!unidadId || !operacionId || !sujetoId || !sujeto) return;
        if (!sujeto.pld_bc_criterio_control || !sujeto.pld_bc_empresa_cliente) {
            await Swal.fire({
                icon: 'warning',
                title: 'Datos incompletos',
                text: 'Capture primero los datos del formato BC (empresa/cliente y criterio de control).',
            });
            setShowModalBc(true);
            return;
        }
        try {
            const [ops, paisesCat] = await Promise.all([
                listOperaciones(unidadId),
                listPaises(unidadId).catch(() => []),
            ]);
            const operacion = Array.isArray(ops)
                ? ops.find((o) => String(o.id) === String(operacionId))
                : null;
            if (!operacion) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Operación',
                    text: 'No se encontró la operación para armar el formato BC.',
                });
                return;
            }
            const blob = await pdf(
                <HojaFormatoBC
                    operacion={operacion}
                    sujetoDetalle={sujeto}
                    origenBc={origenBC}
                    paises={Array.isArray(paisesCat) ? paisesCat : []}
                />
            ).toBlob();
            const nombreArchivo = `formato-bc-${(sujeto.rfc || sujetoId).replace(/[^\w.-]/g, '_')}.pdf`;
            const file = new File([blob], nombreArchivo, { type: 'application/pdf' });
            const res = await uploadDocumento(unidadId, sujetoId, 'formato_bc_fisico', file);
            if (res && res.success) {
                setDocsSujeto(res.nuevoStatus || {});
                setSujeto((prev) =>
                    prev ? { ...prev, documentos_status: res.nuevoStatus || {} } : prev
                );
                await Swal.fire({
                    icon: 'success',
                    title: 'Formato BC generado',
                    text: 'El PDF de Beneficiario Controlador quedó asociado al expediente.',
                });
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: 'Generación',
                    text: (res && res.error) || 'No se pudo guardar el PDF en el servidor.',
                });
            }
        } catch (err) {
            console.error(err);
            await Swal.fire({
                icon: 'error',
                title: 'Generación',
                text:
                    (err &&
                        err.response &&
                        err.response.data &&
                        err.response.data.error) ||
                    err.message ||
                    'Error al generar el formato BC.',
            });
        }
    }

    if (cargando) {
        return (
            <Container className="py-5 text-center">
                <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-muted" />
            </Container>
        );
    }

    if (!sujeto) {
        return (
            <Container className="py-4">
                <Header>Ficha de sujeto</Header>
                <p className="text-muted">No se encontró el sujeto.</p>
                <Button
                    color="link"
                    type="button"
                    className="pl-0"
                    onClick={() => history.push(`/${unidadId}/pld/${operacionId}`)}
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Volver a la operación
                </Button>
            </Container>
        );
    }

    const nombreCabecera =
        `${sujeto.nombre || ''} ${sujeto.paterno || ''} ${sujeto.materno || ''}`.trim();

    return (
        <Container>
            <Button
                color="link"
                type="button"
                className="pl-0 mb-2"
                onClick={() => history.push(`/${unidadId}/pld/${operacionId}`)}
            >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Volver a la operación
            </Button>
            <Header>Ficha de sujeto PLD</Header>

            {/* ── Perfil de Riesgo Inherente EBR ── */}
            <CardEbrInherente registro={ebrRegistro} />

            <Card className="shadow-sm mb-4">
                <CardHeader className="d-flex flex-wrap justify-content-between align-items-center bg-light">
                    <div className="d-flex align-items-center">
                        <div
                            className={`rounded-circle text-white p-3 mx-3 ${perfilActual === 'SOLO_DATOS' ? 'bg-secondary' : 'bg-blue'
                                }`}
                        >
                            <FontAwesomeIcon
                                icon={sujeto.tipo_persona === 'Moral' ? faBuilding : faUser}
                                size="lg"
                            />
                        </div>
                        <div>
                            <h4 className="mb-1 text-uppercase">{nombreCabecera}</h4>
                            <div className="d-flex flex-wrap align-items-center">
                                <Badge color="info" pill className="mr-2 mb-1">
                                    Rol: {rolSujeto || '—'}
                                </Badge>
                                <Badge
                                    color={perfilActual === 'SOLO_DATOS' ? 'secondary' : 'primary'}
                                    pill
                                    className="mr-2 mb-1 text-uppercase"
                                >
                                    Perfil: {perfilActual.replace(/_/g, ' ')}
                                </Badge>
                                {esBC ? (
                                    <Badge color="warning" pill className="mb-1 text-uppercase">
                                        <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
                                        BC: {origenBC || 'Identificado'}
                                    </Badge>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    {perfilActual !== 'SOLO_DATOS' && puedeDocumentos ? (
                        <Button color="success" size="sm" type="button" onClick={cotejarTodos}>
                            <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                            Cotejar todos
                        </Button>
                    ) : null}
                </CardHeader>
                <CardBody>
                    <Row>
                        <Col lg="7">
                            {perfilActual === 'SOLO_DATOS' ? (
                                <div className="text-center py-5 border rounded bg-light">
                                    <FontAwesomeIcon
                                        icon={faExclamationTriangle}
                                        size="3x"
                                        className="text-muted mb-3"
                                    />
                                    <h5 className="text-muted">Perfil de solo datos</h5>
                                    <p className="small text-muted mb-0">
                                        No se exigen documentos para este perfil según la ley.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <h6 className="font-weight-bold text-secondary mb-3">
                                        <FontAwesomeIcon icon={faFileAlt} className="mr-2 text-primary" />
                                        Archivos del expediente
                                    </h6>
                                    {obtenerChecklist().map((doc) => {
                                        const esReportePld = doc.esReportePld === true;
                                        const esFormatoId = doc.id === 'formato_id_fisico' || doc.id === 'formato_bc_fisico';
                                        const data = docsSujeto[doc.id] || {};
                                        const tieneArchivo = esReportePld
                                            ? pldReporteDisponible
                                            : !!data.url;
                                        const esFisico = data.fisico;
                                        const estaCotejado = data.cotejado;
                                        return (
                                            <Card
                                                key={doc.id}
                                                className="mb-2 border shadow-sm"
                                            >
                                                <CardBody className="py-3">
                                                    <Row className="align-items-center">
                                                        <Col md="4">
                                                            <div className="font-weight-bold small">
                                                                {doc.titulo}
                                                            </div>
                                                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                                {doc.desc}
                                                            </div>
                                                        </Col>
                                                        <Col md="8" className="mt-2 mt-md-0">
                                                            <div className="d-flex flex-wrap align-items-center">
                                                                {tieneArchivo ? (
                                                                    <Badge color={estaCotejado ? 'success' : 'gray'} pill className="mr-2 mb-1">
                                                                        Digital
                                                                    </Badge>
                                                                ) : esFisico ? (
                                                                    <Badge color={estaCotejado ? 'success' : 'gray'} pill className="mr-2 mb-1">
                                                                        Físico
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge
                                                                        pill
                                                                        className="mr-2 mb-1 border"
                                                                    >
                                                                        Pendiente
                                                                    </Badge>
                                                                )}
                                                                <div className="d-flex align-items-center bg-light border rounded px-2 py-1 mr-2 mb-1">
                                                                    {!tieneArchivo &&
                                                                        !esReportePld &&
                                                                        puedeDocumentos ? (
                                                                        <CustomInput
                                                                            hidden
                                                                            type="checkbox"
                                                                            id={`fis-${doc.id}`}
                                                                            label="Físico"
                                                                            checked={!!esFisico}
                                                                            onChange={() =>
                                                                                togglePropiedad(doc.id, 'fisico')
                                                                            }
                                                                            className="mb-0"
                                                                        />
                                                                    ) : null}
                                                                    {puedeDocumentos ? (
                                                                        <CustomInput
                                                                            type="checkbox"
                                                                            id={`cot-${doc.id}`}
                                                                            label="Cotejado"
                                                                            checked={!!estaCotejado}
                                                                            onChange={() =>
                                                                                togglePropiedad(doc.id, 'cotejado')
                                                                            }
                                                                            className="mb-0 ml-2"
                                                                            disabled={!tieneArchivo}
                                                                        />
                                                                    ) : null}
                                                                </div>
                                                                {tieneArchivo ? (
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        {esReportePld ? (
                                                                            <Button
                                                                                color="light"
                                                                                size="sm"
                                                                                className="mr-1"
                                                                                type="button"
                                                                                onClick={abrirReportePldPdf}
                                                                            >
                                                                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                                                                            </Button>
                                                                        ) : (
                                                                            <>
                                                                                <Button
                                                                                    color="light"
                                                                                    size="sm"
                                                                                    className="mr-1"
                                                                                    tag="a"
                                                                                    href={resolvePldDocumentoFetchUrl(unidadId, sujetoId, data)}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                >
                                                                                    <FontAwesomeIcon
                                                                                        icon={faExternalLinkAlt}
                                                                                    />
                                                                                </Button>
                                                                                {puedeDocumentos && !esFormatoId ? (
                                                                                    <Label
                                                                                        className="btn btn-light btn-sm mb-0"
                                                                                        hidden={estaCotejado}
                                                                                    >
                                                                                        <FontAwesomeIcon
                                                                                            icon={faCloudUploadAlt}
                                                                                        />
                                                                                        <input
                                                                                            type="file"
                                                                                            className="d-none"
                                                                                            tooltip="Reemplazar archivo"
                                                                                            onChange={(e) =>
                                                                                                handleUpload(e, doc.id)
                                                                                            }
                                                                                        />
                                                                                    </Label>
                                                                                ) : null}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                ) : puedeDocumentos && !esReportePld && !esFormatoId ? (
                                                                    <Label
                                                                        className={`btn btn-outline-primary btn-sm mb-1 ${subiendoId === doc.id ? 'disabled' : ''
                                                                            }`}
                                                                    >
                                                                        {subiendoId === doc.id ? (
                                                                            <FontAwesomeIcon icon={faSpinner} spin />
                                                                        ) : (
                                                                            <FontAwesomeIcon icon={faCloudUploadAlt} />
                                                                        )}{' '}
                                                                        Subir
                                                                        <input
                                                                            type="file"
                                                                            className="d-none"
                                                                            onChange={(e) =>
                                                                                handleUpload(e, doc.id)
                                                                            }
                                                                        />
                                                                    </Label>
                                                                ) : null}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </CardBody>
                                            </Card>
                                        );
                                    })}
                                </>
                            )}
                        </Col>
                        <Col lg="5">
                            <Card className="border-primary bg-light">
                                <CardHeader className="bg-primary text-white py-2">
                                    <strong className="small text-uppercase">Formatos Identificación Cliente</strong>
                                </CardHeader>
                                <CardBody>
                                    {!esBC && (
                                        <Button
                                            color="light"
                                            block
                                            className="p-2 mt-2 mx-3 text-left border"
                                            type="button"
                                            disabled={!puedeSujetos}
                                            onClick={() => setShowModalId(true)}
                                        >
                                            <span className="d-flex justify-content-between align-items-center small font-weight-bold">
                                                Captura datos identificación
                                            </span>
                                        </Button>
                                    )}
                                    {!esBC && perfilActual !== 'SOLO_DATOS' && puedeDocumentos ? (
                                        <Button
                                            color="danger"
                                            block
                                            className="mt-2 font-weight-bold small"
                                            type="button"
                                            onClick={generarPDFFormato}
                                        >
                                            Generar formato ID
                                        </Button>
                                    ) : null}
                                    {esBC ? (
                                        <Button
                                            color="light"
                                            block
                                            className="p-2 mx-3 mt-2 text-left border"
                                            type="button"
                                            disabled={!puedeSujetos}
                                            onClick={() => setShowModalBc(true)}
                                        >
                                            <span className="d-flex justify-content-between align-items-center small font-weight-bold">
                                                Captura datos dueño beneficiario
                                            </span>
                                        </Button>
                                    ) : null}
                                    {esBC && perfilActual !== 'SOLO_DATOS' && puedeDocumentos ? (
                                        <Button
                                            color="danger"
                                            block
                                            className="mt-2 font-weight-bold small"
                                            type="button"
                                            onClick={generarPDFFormatoBC}
                                        >
                                            Generar formato BC
                                        </Button>
                                    ) : null}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {showModalId && sujeto && (
                <ModalIdentificacion
                    unidadId={unidadId}
                    sujeto={sujeto}
                    readOnly={!puedeSujetos}
                    onClose={() => setShowModalId(false)}
                    onSave={(newData) => {
                        setSujeto((prev) => ({ ...prev, ...newData }));
                        if (newData.documentos_status) {
                            setDocsSujeto(newData.documentos_status);
                        }
                    }}
                />
            )}

            {showModalBc && sujeto && (
                <ModalFormatoBC
                    unidadId={unidadId}
                    sujeto={sujeto}
                    readOnly={!puedeSujetos}
                    empresaClienteSugerida={
                        sujeto.pld_bc_empresa_cliente || empresaClienteSugerida
                    }
                    origenBc={origenBC}
                    onClose={() => setShowModalBc(false)}
                    onSave={(newData) => {
                        setSujeto((prev) => ({ ...prev, ...newData }));
                        if (newData.documentos_status) {
                            setDocsSujeto(newData.documentos_status);
                        }
                    }}
                />
            )}
        </Container>
    );
}
